import React from 'react'
import PropTypes from 'prop-types'
import { dataProps, onProps } from '@ds/react-utils'
import { CustomPropTypes } from '../../../support'
import { useThemeStyles } from '../../../theming'
import type { TextAreaForwardRef } from '../../../types'
import { variant, removeStyleProps } from '../../../utilities'
import styles from './styles'
import { useFormContext } from '../../../components/FormContext/FormContext'

export type ResizeValue = 'both' | 'horizontal' | 'none' | 'vertical'

export interface InputTextAreaProps
  extends Omit<React.ComponentPropsWithoutRef<'textarea'>, 'children'> {
  /**
   * The 'id' for the aria-describedby and associated error.
   */
  ariaDescribedById?: string
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * A React ref to assign to the HTML node representing the TextArea element.
   */
  forwardedRef?: TextAreaForwardRef
  /**
   * The CSS height of the rendered `<textarea>`.
   */
  height?: string
  /**
   * An invalid state on one of the children form elements.
   */
  invalid?: boolean
  /**
   * The options for the ability for resizing of the TextArea.
   * Defaults to true for both vertical and horizontal.
   */
  resize?: ResizeValue
  /**
   * The width of the textarea element.
   */
  width?: string
}

export function InputTextArea({
  ariaDescribedById,
  disabled = false,
  forwardedRef,
  height,
  invalid = false,
  maxLength = 0,
  readOnly = false,
  required = false,
  resize = 'both',
  rows,
  width,
  ...restProps
}: InputTextAreaProps) {
  const formContext = useFormContext()

  const sx = useThemeStyles(styles, { rows })

  const resizeVariant = variant('resize', resize)

  const textareaStyles = [
    sx.default.textarea,
    invalid && sx.invalid.textarea,
    rows === 1 ? sx.resizeNone : sx[resizeVariant],
  ]

  return (
    <textarea
      {...dataProps(restProps)}
      {...onProps(restProps)}
      {...removeStyleProps(restProps)}
      aria-describedby={ariaDescribedById}
      aria-invalid={invalid ? true : undefined}
      aria-required={
        formContext?.requiredAttribute ? undefined : required || undefined
      }
      css={textareaStyles}
      disabled={disabled}
      maxLength={maxLength || undefined}
      readOnly={readOnly}
      ref={forwardedRef}
      required={formContext?.requiredAttribute ? required : undefined}
      rows={rows}
      style={{ height, width }}
    />
  )
}

InputTextArea.resizeValues = ['both', 'horizontal', 'none', 'vertical']

InputTextArea.propTypes = {
  ariaDescribedById: PropTypes.string,
  'data-.*': PropTypes.string,
  forwardedRef: CustomPropTypes.ReactRef,
  height: PropTypes.string,
  invalid: PropTypes.bool,
  resize: PropTypes.oneOf(InputTextArea.resizeValues),
  width: PropTypes.string,
}

InputTextArea.displayName = 'InputTextArea'
